<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import Hls from 'hls.js';

const props = defineProps<{
    videoUrl: string;
}>();

const emit = defineEmits<{
    (e: 'play'): void;
    // The stream can't be played (fatal HLS error, media error, or no HLS
    // support), so the parent can stop waiting on `play`.
    (e: 'error'): void;
}>();

const videoRef = ref<HTMLVideoElement | null>(null);
let hls: Hls | null = null;

// Convert Cloudflare watch/iframe URL to HLS manifest
// e.g. https://customer-xxx.cloudflarestream.com/{id}/watch → .../manifest/video.m3u8
const toHlsUrl = (url: string): string =>
    url.replace(/\/watch$/, '/manifest/video.m3u8');

const initPlayer = () => {
    if (!videoRef.value || !props.videoUrl) return;

    destroyPlayer();

    const hlsUrl = toHlsUrl(props.videoUrl);

    if (Hls.isSupported()) {
        hls = new Hls();
        hls.on(Hls.Events.ERROR, (_event, data) => {
            if (data.fatal) emit('error');
        });
        hls.loadSource(hlsUrl);
        hls.attachMedia(videoRef.value);
    } else if (videoRef.value.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari has native HLS support
        videoRef.value.src = hlsUrl;
    } else {
        emit('error');
    }
};

const destroyPlayer = () => {
    if (hls) {
        hls.destroy();
        hls = null;
    }
};

// In the HLS.js path, fatal errors are already reported via
// hls.on(Hls.Events.ERROR), and a media error can also surface natively.
// Only propagate native errors when HLS.js isn't in charge, to avoid
// emitting twice for one failure.
const onNativeVideoError = () => {
    if (!hls) emit('error');
};

onMounted(() => initPlayer());

watch(() => props.videoUrl, () => initPlayer());

onBeforeUnmount(() => destroyPlayer());
</script>

<template>
    <video
        ref="videoRef"
        controls
        preload="metadata"
        class="cloudflare-player"
        @play="emit('play')"
        @error="onNativeVideoError"
    />
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.cloudflare-player {
    width: 100%;
    border-radius: $portal-card-radius;
    overflow: hidden;
    display: block;
}
</style>
